import { Accordion, Alert, AlertIcon, Button, Heading, HStack, Spinner, Stack, Text, useToast } from '@chakra-ui/react';
import { TenantId } from '@pelicargo/tenants';
import { Show, useError, useSuccess } from '@pelicargo/ui';
import { useEffect, useMemo } from 'react';
import { FieldErrors, useForm, useWatch } from 'react-hook-form';

import { SimpleForm } from '../components/SimpleForm';
import { SimpleSelect } from '../components/SimpleSelect';
import {
  clearSection,
  themeFormResolver,
  ThemeFormValues,
  toFieldIssues,
  toFormPath,
  toFormValues,
} from '../components/Theme/form';
import { THEME_SECTIONS, ThemeSectionName } from '../components/Theme/sections';
import { ThemeSectionPanel } from '../components/Theme/ThemeSectionPanel';
import { useAuth } from '../context/AuthProvider';
import { useCustomerTheme } from '../hooks/tenant/useCustomerTheme';

type PickerValues = {
  tenant_id: TenantId;
  source_tenant_id?: TenantId;
};

export const ThemeConfiguration = () => {
  const { knownTenants } = useAuth();
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();
  const toast = useToast();

  const pickerMethods = useForm<PickerValues>({
    defaultValues: { tenant_id: 'pelicargo', source_tenant_id: undefined },
  });
  const tenantId = useWatch({ control: pickerMethods.control, name: 'tenant_id' });
  const sourceTenantId = useWatch({ control: pickerMethods.control, name: 'source_tenant_id' });

  const { theme, isLoading, isSaving, save } = useCustomerTheme(tenantId);
  const { theme: sourceTheme } = useCustomerTheme(sourceTenantId);

  const methods = useForm<ThemeFormValues>({ resolver: themeFormResolver, defaultValues: toFormValues(null) });

  // Re-seed whenever the selected tenant's theme lands. `toFormValues` is also what guarantees
  // every nested object exists before the fields register — see its comment.
  useEffect(() => {
    methods.reset(toFormValues(theme));
  }, [theme, methods]);

  const tenantOptions = useMemo(() => knownTenants.map((tenant) => ({ label: tenant, value: tenant })), [knownTenants]);

  const applyServerIssues = (error: unknown) => {
    for (const issue of toFieldIssues(error)) {
      const path = toFormPath(issue.path);
      if (!path) continue;

      methods.setError(path as never, { type: 'server', message: issue.message });
    }
  };

  const handleSave = methods.handleSubmit(
    async (values) => {
      try {
        await save(values);
        handleSuccess('Theme saved');
      } catch (error) {
        applyServerIssues(error);
        handleError(error);
      }
    },
    // A collapsed panel hides the control an error would hang off, so without this a rejected
    // submit would just do nothing. Same guard as MarkupConfigurationDrawer.
    (errors: FieldErrors<ThemeFormValues>) => {
      toast({
        title: 'This theme has some errors',
        description: `Check: ${Object.keys(errors).join(', ')}`,
        status: 'error',
        duration: 6000,
      });
    },
  );

  const handleResetSection = async (section: ThemeSectionName) => {
    methods.reset(clearSection(methods.getValues(), section));
    await handleSave();
  };

  const handleRevert = () => methods.reset(toFormValues(theme));

  const handleCopyFromTenant = () => {
    methods.reset(toFormValues(sourceTheme), { keepDefaultValues: true });
    handleSuccess(`Loaded the ${sourceTenantId} theme — review it, then save`);
  };

  const isDirty = methods.formState.isDirty;
  const showLoading = isLoading && !!tenantId;
  const canEditTheme = !!tenantId && !isLoading;
  const canSave = isDirty && !isSaving;
  const canCopyFromTenant = !!sourceTheme && !!sourceTenantId && sourceTenantId !== tenantId;

  return (
    <Stack spacing="2" maxH="100vh" h="100vh" overflowY="auto" p="4">
      <Stack borderBottom="1px solid" borderColor="blackAlpha.500" pb="2">
        <Heading variant="h5">Customer Theme</Heading>
        <Text fontSize="sm">
          Per-tenant overrides for the customer app. Every field is optional: leaving one blank is not &quot;no
          value&quot;, it removes the override entirely and the tenant falls back to the look that ships with the build.
          Clearing a field and saving is how you undo a change.
        </Text>
        <SimpleForm submitOnBlur={false} {...pickerMethods}>
          <HStack align="flex-end" spacing="4">
            <Stack w="60" spacing="0">
              <Text fontSize="xs">Tenant</Text>
              <SimpleSelect name="tenant_id" options={tenantOptions} />
            </Stack>
            <Stack w="60" spacing="0">
              <Text fontSize="xs">Copy from tenant…</Text>
              <SimpleSelect name="source_tenant_id" options={tenantOptions} />
            </Stack>
            <Button size="sm" variant="outline" isDisabled={!canCopyFromTenant} onClick={handleCopyFromTenant}>
              Copy
            </Button>
          </HStack>
        </SimpleForm>
      </Stack>

      <Show if={showLoading}>
        <Spinner alignSelf="center" my="8" />
      </Show>

      <Show if={canEditTheme}>
        <SimpleForm submitOnBlur={false} {...methods}>
          <Stack spacing="3" pb="4">
            <Show if={isDirty}>
              <Alert status="warning">
                <AlertIcon />
                <Text fontSize="sm">Unsaved changes. Nothing reaches the customer app until you save.</Text>
              </Alert>
            </Show>

            <HStack spacing="3">
              <Button size="sm" colorScheme="brand" isDisabled={!canSave} isLoading={isSaving} onClick={handleSave}>
                Save
              </Button>
              <Button size="sm" variant="outline" isDisabled={!isDirty} onClick={handleRevert}>
                Revert changes
              </Button>
            </HStack>

            <Accordion allowMultiple defaultIndex={[0]}>
              {THEME_SECTIONS.map((section) => (
                <ThemeSectionPanel
                  key={section.name}
                  section={section}
                  isDisabled={isSaving}
                  tenantId={tenantId}
                  onResetSection={handleResetSection}
                />
              ))}
            </Accordion>
          </Stack>
        </SimpleForm>
      </Show>
    </Stack>
  );
};
